import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SystemUsers component
 *
 * This component displays and manages system users for the Guild client.
 * For non-Guild clients, use the `ClientUsers` component instead.
 *
 */
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav/AdminTopNav';
import { CREATE_USER, UPDATE_USER, USERS } from '@src/graphql/queries/user';
import { useAuth } from '@src/hooks/useAuth';
import { useUsers } from '@src/hooks/useUsers';
import { format, isValid, parseISO } from 'date-fns';
import { pick } from 'lodash';
import { DataTable, Form, MfaMode, Permission, Role, SearchFilterBar, TrackableButton, trimObjectStringFields, useAlert, useLocale, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
export const SystemUsers = ({ client }) => {
    const { hasPermission } = useAuth();
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const refetchQueries = (result) => {
        if (!result.data)
            return [];
        if (result.data.createUser?.validationErrors ||
            result.data.createUser?.error ||
            result.data.updateUser?.validationErrors ||
            result.data.updateUser?.error) {
            return [];
        }
        return [USERS];
    };
    const [searchText, setSearchText] = useState('');
    const { users, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, } = useUsers({
        clientId: client.id,
        searchText,
    });
    const [createUser] = useMutation(CREATE_USER, {
        refetchQueries,
    });
    const [updateUser] = useMutation(UPDATE_USER, {
        refetchQueries,
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [saveError, setSaveError] = useState('');
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const UserFields = useMemo(() => {
        const fields = [
            {
                name: 'firstName',
                label: t('users.fields.firstName'),
                type: 'text',
            },
            {
                name: 'lastName',
                label: t('users.fields.lastName'),
                type: 'text',
            },
            {
                name: 'email',
                label: t('users.fields.email'),
                type: 'text',
            },
            {
                name: 'isActive',
                label: t('users.fields.isActive'),
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
            },
            {
                name: 'mfasEnabled',
                label: t('users.fields.mfasEnabled'),
                type: 'lookup',
                multiple: true,
                options: Object.keys(MfaMode).map(mode => ({
                    value: mode,
                    label: t(mode),
                })),
            },
            {
                name: 'roleNames',
                label: t('users.fields.roleNames'),
                type: 'lookup',
                multiple: true,
                options: [
                    { value: Role.SYS_ADMIN, label: t(Role.SYS_ADMIN) },
                    { value: Role.CLAIMS_PROCESSOR, label: t(Role.CLAIMS_PROCESSOR) },
                    { value: Role.MEMBER_SUPPORT, label: t(Role.MEMBER_SUPPORT) },
                ],
            },
            {
                name: 'password',
                label: t('users.fields.password'),
                type: 'password',
            },
        ];
        return fields;
    }, [t]);
    const handleValidate = (user) => {
        const errors = {};
        user = trimObjectStringFields(user, ['firstName', 'lastName', 'email', 'uniqueId']);
        if (!user.firstName) {
            errors.firstName = t('First Name is required');
        }
        if (!user.lastName) {
            errors.lastName = t('Last Name is required');
        }
        if (!user.email) {
            errors.email = t('Email is required');
        }
        if (!user.roleNames || user.roleNames.length === 0) {
            errors.roleNames = t('Role is required');
        }
        if (!user.id) {
            if (!user.password) {
                errors.password = t('Password is required');
            }
        }
        return errors;
    };
    const handleSave = async (user) => {
        let result;
        try {
            const mutation = user.id ? updateUser : createUser;
            const prop = user.id ? 'updateUser' : 'createUser';
            const errors = handleValidate(user);
            if (Object.keys(errors).length > 0) {
                setSaveError(Object.values(errors).join(', '));
                return;
            }
            setSaveError('');
            result = await mutation({
                variables: {
                    input: {
                        ...pick(user, [
                            'clientId',
                            'firstName',
                            'lastName',
                            'email',
                            'mfasEnabled',
                            'roleNames',
                            'isActive',
                            'disableAt',
                        ]),
                        id: user.id ? user.id : undefined,
                        password: user.password ? user.password : undefined,
                    },
                },
            });
            if (result.data) {
                if (result.data[prop]?.validationErrors) {
                    return result.data[prop]?.validationErrors;
                }
                if (result.data[prop]?.error) {
                    setSaveError(t(result.data[prop].error));
                    return;
                }
            }
            showAlert(t('User saved'));
            setSelectedUser(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const msg = e.message;
            setSaveError(msg);
        }
    };
    const getRoles = (user) => {
        const isSuperuser = user.isSuperuser;
        const roles = (isSuperuser ? [Role.SYS_ADMIN.toString()] : []).concat((user.roleNames || []));
        return roles;
    };
    const handleAddUser = () => {
        setSelectedUser({
            id: 0,
            clientId: client.id || 0,
            firstName: '',
            lastName: '',
            email: '',
            isActive: true,
            isVerified: false,
            isSuperuser: false,
            mfasEnabled: [],
        });
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('System Users'), searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, searchText: searchText, onSearchChange: text => {
                        setSearchText(text);
                        setPage(1);
                    }, searchPlaceholder: t('Search by Name/Email') }), actions: hasPermission(Permission.CREATE_USER) && (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add User', tag: 'header', color: 'primary', variant: 'contained', type: 'button', className: 'button button--primary margin--bottom_16', onClick: handleAddUser, children: t('users.add') }) })) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsxs(_Fragment, { children: [_jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: users, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                            setPage(pagination.page + 1);
                                            setPageSize(pagination.pageSize);
                                        }, onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'firstName', sort: orderByDir }], rowCount: total, loading: loading, columns: [
                                            {
                                                field: 'firstName',
                                                headerName: t('First Name'),
                                                flex: 0.2,
                                            },
                                            {
                                                field: 'lastName',
                                                headerName: t('Last Name'),
                                                flex: 0.2,
                                            },
                                            {
                                                field: 'email',
                                                headerName: t('Email'),
                                                flex: 0.2,
                                            },
                                            {
                                                field: 'isActive',
                                                headerName: t('Active?'),
                                                valueGetter: e => (e.row.isActive ? 'Yes' : 'No'),
                                                flex: 0.2,
                                                sortable: false,
                                            },
                                            {
                                                field: 'roleNames',
                                                headerName: t('Roles'),
                                                flex: 0.2,
                                                sortable: false,
                                                renderCell: ({ row }) => {
                                                    return getRoles(row)
                                                        .map(r => t(r))
                                                        .join(', ');
                                                },
                                            },
                                            {
                                                field: 'disableAt',
                                                headerName: t('Disable At'),
                                                flex: 0.2,
                                                sortable: false,
                                                renderCell: ({ row }) => {
                                                    if (!row.disableAt)
                                                        return '';
                                                    const date = parseISO(row.disableAt);
                                                    return isValid(date) ? format(date, 'MM-dd-yyyy') : row.disableAt;
                                                },
                                            },
                                            {
                                                field: 'action',
                                                headerName: t('Actions'),
                                                renderCell: ({ row }) => (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `user_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedUser(row), className: 'margin--right_8' }) })),
                                                width: 100,
                                                sortable: false,
                                            },
                                        ] }), selectedUser && (_jsx(Form, { title: selectedUser.id ? 'Edit User' : 'Add User', modal: true, obj: { ...selectedUser, roleNames: getRoles(selectedUser) }, fields: UserFields, onValidate: handleValidate, onSave: handleSave, onCancel: () => {
                                            setSelectedUser(null);
                                            setSaveError('');
                                        }, error: saveError }))] })] }) }) })] }));
};
